import { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useSecretCode, useKonami } from '@bobex/react-core/utils/hooks/useSecretCode';
import { showNedry } from '@bobex/react-core/components/Nedry/showNedry';
import { AccountService } from '@bobex/react-core/services/AccountService';
import { matrixRain } from './matrixRain';
import { startSnow, snowHeavier, stopSnow, isSnowing, isSnowDiscovered, markSnowDiscovered } from './snow';
import { startFire, isBurning } from './fire';
import { startShire, stopShire, isShireActive, applyPersistedShire, markShireDiscovered } from './shire';
import { Credits } from './Credits';
import { StarWarsCrawl } from './StarWarsCrawl';
import { godModeStore } from './godmode/godModeStore';
import { achievementsStore } from './achievements/achievementsStore';
import { notifyAchievement } from './achievements/notifyAchievement';
import { AchievementsModal } from './achievements/AchievementsModal';

// Non-intrusive keyboard easter eggs. Type the word anywhere outside an input to trigger.
// (Nedry also appears on his own when a forbidden 403 response comes back.)
export function EasterEggs() {
    // Finding any egg switches on achievement tracking and unlocks "Clever Girl" (once).
    const discover = () => {
        achievementsStore.markDiscovered();
        notifyAchievement('clever-girl');
    };

    // Starts a timed, cosmetic-only God Mode session (triggered by several code words).
    const activateGodMode = () => {
        discover();
        godModeStore.activate(AccountService.getUser()?.departmentId ?? undefined);
        confetti({
            particleCount: 160,
            spread: 100,
            startVelocity: 45,
            origin: { y: 0.6 },
            colors: ['#FFD700', '#FFC107', '#FF9800'],
        });
        notifications.show({
            title: '⚡ GOD MODE ENABLED',
            message: 'Invincible… cosmetically. 🛡️⚡',
            color: 'grape',
        });
    };

    // "melt" / "enough" / "stop" / "sun" — stop any snowfall.
    const stopSnowNow = () => {
        if (!isSnowing()) return;
        stopSnow();
        notifications.show({ title: '☀️ Spring has arrived.', message: 'The snow melts away.', color: 'yellow' });
    };

    // "stop" / "enough" — a general calm-down: end both the snow and the Shire/Rivendell theme.
    const calmDown = () => {
        stopSnowNow();
        if (isShireActive()) stopShire();
    };

    // The dramatic snow discovery ("winter is coming", also the no-spaces variant).
    const startWinter = () => {
        discover();
        markSnowDiscovered();
        startSnow({ dramatic: true });
    };

    // 🔥 "dracarys" — a rage-release: the screen burns to ash, then rebuilds. If snow is already
    // falling, the fire turns blue: the secret third effect, "a song of ice and fire".
    const rageRelease = () => {
        discover();
        if (isBurning()) return;
        const iceAndFire = isSnowing();
        if (iceAndFire) {
            stopSnow(); // the snow freezes over and stops before the flames rise
            notifyAchievement('ice-and-fire');
        }
        startFire({
            blue: iceAndFire,
            title: iceAndFire ? '🧊🔥 A SONG OF ICE AND FIRE' : undefined,
            subtitle: iceAndFire ? "You weren't supposed to find this." : undefined,
            onRestored: iceAndFire
                ? undefined
                : () => notifications.show({
                    title: '🔥 Rebuilt from the ashes',
                    message: 'The Backoffice rises again. But winter is coming…',
                    color: 'orange',
                }),
        });
    };

    // Restore a kept Shire/Rivendell theme on load (a user can choose to keep it — see God Mode).
    useEffect(() => { applyPersistedShire(); }, []);

    // God Mode ends on its own after a few minutes → mortal-again message + "Mere Mortal".
    useEffect(() => godModeStore.onExpire(() => {
        notifications.show({
            title: '⚡ GOD MODE EXPIRED',
            message: 'You are mortal again. Please proceed with caution.',
            color: 'grape',
        });
        notifyAchievement('mere-mortal');
    }), []);

    // Konami code (↑↑↓↓←→←→BA) → Matrix "digital rain" for a few seconds.
    useKonami(() => {
        discover();
        matrixRain();
    });

    useSecretCode({
        // 🦖 "nedry" — summon him on demand (same guy the 403 handler shows).
        nedry: () => {
            discover();
            showNedry(true);
        },
        // ☕ "coffee" — a little break.
        coffee: () => {
            discover();
            confetti({ particleCount: 80, spread: 70, origin: { y: 0.7 }, scalar: 1.1 });
            notifications.show({
                title: '☕ Break time',
                message: 'Grab a coffee — you earned it.',
                color: 'yellow',
            });
        },
        // 🛡️ God Mode — Doom's "iddqd" and the on-brand "bobex".
        iddqd: activateGodMode,
        bobex: activateGodMode,
        // 🏆 "trophy" — open the personal achievements screen.
        trophy: () => {
            discover();
            modals.open({ title: '🏆 My Achievements', centered: true, size: 'md', children: <AchievementsModal /> });
        },
        // ❄️ "winter is coming" — the dramatic discovery: cool vignette, a fading title, auto-melts.
        'winter is coming': startWinter,
        winteriscoming: startWinter,
        // ❄️ "snow" — quick toggle, but only once discovered via "winter is coming". Repeats get heavier.
        snow: () => {
            if (!isSnowDiscovered()) return;
            discover();
            if (!isSnowing()) {
                startSnow();
                notifications.show({ title: '❄️ Let it snow.', message: 'Winter mode engaged.', color: 'blue' });
            } else if (snowHeavier() >= 3) {
                notifications.show({ title: '🌨️ BLIZZARD MODE', message: 'This was a poor decision.', color: 'blue' });
            }
        },
        // 🔥 "dracarys" — burn the current screen to ash (blue fire if it's already snowing).
        dracarys: rageRelease,
        // 🌿 "shire" / "rivendell" — reskin the Backoffice into a calm woodland sanctuary.
        shire: () => { discover(); markShireDiscovered(); startShire('shire'); },
        rivendell: () => { discover(); markShireDiscovered(); startShire('rivendell'); },
        // ☀️ "melt" / "enough" / "stop" / "sun" — stop the snow (stop/enough also leave the sanctuary).
        melt: stopSnowNow,
        enough: calmDown,
        stop: calmDown,
        sun: stopSnowNow,
        // 🤵/🌌 "credits" — coin flip: a bowing butler presents the team, or a Star Wars crawl does.
        credits: () => {
            discover();
            const starwars = Math.random() < 0.5;
            modals.open({
                title: starwars ? undefined : 'Bobex Justice League',
                centered: true,
                size: starwars ? 'lg' : 'auto',
                padding: starwars ? 0 : undefined,
                styles: starwars
                    ? {
                        content: { background: '#000' },
                        header: { background: '#000', padding: '10px 14px' },
                        close: { color: '#feda4a' },
                    }
                    : undefined,
                children: starwars ? <StarWarsCrawl /> : <Credits />,
            });
        },
    });

    return null;
}
